import { useRef, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';

// Controlled 6-digit OTP input rendered as N separate boxes.
// Auto-advances to the next box on entry, jumps back on Backspace,
// and accepts a 6-digit paste in any cell.
const OtpInput = ({
  value = '',
  onChange,
  length = 6,
  disabled = false,
  onComplete,
  autoFocus = true,
}) => {
  const refs = useRef([]);

  // Pad / clip the controlled value to N chars so refs always line up.
  const digits = value.padEnd(length, ' ').slice(0, length).split('');

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  // Notify parent when all digits are filled
  useEffect(() => {
    if (value.length === length && /^\d+$/.test(value)) {
      onComplete?.(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const set = useCallback(
    (next) => {
      const cleaned = next.replace(/\D/g, '').slice(0, length);
      onChange?.(cleaned);
    },
    [length, onChange]
  );

  const handleChange = (i) => (e) => {
    const ch = e.target.value.replace(/\D/g, '').slice(-1); // last digit only
    if (!ch) return;
    const arr = digits.map((d) => (d === ' ' ? '' : d));
    arr[i] = ch;
    const joined = arr.join('').slice(0, length);
    set(joined);
    // advance focus
    if (i < length - 1) refs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i) => (e) => {
    if (e.key === 'Backspace') {
      const arr = digits.map((d) => (d === ' ' ? '' : d));
      if (arr[i]) {
        arr[i] = '';
        set(arr.join(''));
      } else if (i > 0) {
        arr[i - 1] = '';
        set(arr.join(''));
        refs.current[i - 1]?.focus();
      }
      e.preventDefault();
    } else if (e.key === 'ArrowLeft' && i > 0) {
      refs.current[i - 1]?.focus();
    } else if (e.key === 'ArrowRight' && i < length - 1) {
      refs.current[i + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const txt = (e.clipboardData || window.clipboardData).getData('text');
    const cleaned = txt.replace(/\D/g, '').slice(0, length);
    if (!cleaned) return;
    e.preventDefault();
    set(cleaned);
    const next = Math.min(cleaned.length, length - 1);
    refs.current[next]?.focus();
  };

  return (
    <Box
      onPaste={handlePaste}
      sx={{
        display: 'flex',
        gap: { xs: 1, sm: 1.25 },
        justifyContent: 'space-between',
      }}
    >
      {Array.from({ length }).map((_, i) => {
        const ch = digits[i] === ' ' ? '' : digits[i];
        return (
          <Box
            key={i}
            ref={(el) => (refs.current[i] = el)}
            component="input"
            type="text"
            inputMode="numeric"
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            maxLength={1}
            value={ch}
            disabled={disabled}
            onChange={handleChange(i)}
            onKeyDown={handleKeyDown(i)}
            onFocus={(e) => e.target.select()}
            sx={{
              flex: 1,
              minWidth: 0,
              height: { xs: 48, sm: 56 },
              textAlign: 'center',
              fontSize: { xs: 20, sm: 24 },
              fontWeight: 700,
              color: 'var(--ink)',
              bgcolor: '#fff',
              border: '1.5px solid var(--border-l)',
              borderRadius: '12px',
              outline: 'none',
              fontFamily: '"Outfit", sans-serif',
              transition: 'border-color 0.15s, box-shadow 0.15s',
              '&:focus': {
                borderColor: 'var(--sage)',
                boxShadow: '0 0 0 3px rgba(106,174,128,0.18)',
              },
              '&:disabled': { bgcolor: 'var(--cream)', color: 'var(--stone)' },
            }}
          />
        );
      })}
    </Box>
  );
};

export default OtpInput;
